import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import Transport from "../../../../../../passapp-sdk/transport";
import {
  TransportAuthorization,
  TransportEventType,
  RequestTransportAuthorizationPayload,
  ApproveTransportAuthorizationPayload,
} from "../../../../../../passapp-sdk/types/transportTypes";
import { useStore } from "../../../../../../hooks/useStore";

/** `id` es el de la autorización creada/afectada: los tres endpoints responden con `data.id`. */
type RequestAuthorizationResult = { success: true; id: string } | { success: false; error: string };

type UseTransportAuthorizationsParams = {
  /** null mientras no haya empresa transportista seleccionada. */
  transportCompany: string | null;
  eventType: TransportEventType;
  /** DNI del chofer tal como viene del formulario; el hook lo normaliza. */
  driverDni?: string;
};

export const useTransportAuthorizations = ({
  transportCompany,
  eventType,
  driverDni,
}: UseTransportAuthorizationsParams) => {
  const { authStore } = useStore();
  const transportApi = useMemo(() => new Transport(), []);

  const [authorizations, setAuthorizations] = useState<TransportAuthorization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);

  // debounce.cancel() no aborta un request ya emitido: sin este guard, una respuesta lenta de
  // una búsqueda anterior puede pisar a la actual.
  const requestIdRef = useRef(0);
  // Un refetch "silencioso" (polling, botón de refrescar) no debe vaciar la lista ni mostrar
  // el loading state: sólo reemplaza los datos una vez que llega la respuesta, sin parpadeo.
  const isSilentRefetchRef = useRef(false);

  const normalizedDni = driverDni?.trim() || undefined;

  const fetchAuthorizations = useCallback(
    debounce(
      async (company: string, type: TransportEventType, dni: string | undefined, token: string) => {
        const requestId = ++requestIdRef.current;
        try {
          const response = await transportApi.getTransportAuthorizations(token, company, type, dni);
          if (requestId !== requestIdRef.current) return;
          setAuthorizations(response?.data ?? []);
          setError(null);
        } catch (err) {
          if (requestId !== requestIdRef.current) return;
          console.error("Error fetching transport authorizations:", err);
          setError(typeof err === "string" ? err : "Error al buscar autorizaciones");
          setAuthorizations([]);
        } finally {
          if (requestId === requestIdRef.current) {
            setIsLoading(false);
          }
        }
      },
      350
    ),
    [transportApi]
  );

  useEffect(() => {
    if (!authStore.token || !transportCompany) {
      requestIdRef.current += 1;
      setAuthorizations([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    const silent = isSilentRefetchRef.current;
    isSilentRefetchRef.current = false;
    if (!silent) {
      // Descartamos lo anterior de inmediato para no mostrar autorizaciones de otra empresa
      // o del otro flujo mientras llega la respuesta nueva.
      setAuthorizations([]);
      setError(null);
      setIsLoading(true);
    }
    fetchAuthorizations(transportCompany, eventType, normalizedDni, authStore.token);
    return () => fetchAuthorizations.cancel();
  }, [
    transportCompany,
    eventType,
    normalizedDni,
    authStore.token,
    fetchAuthorizations,
    refetchKey,
  ]);

  // Silencioso por defecto: un refetch disparado por una acción (solicitar, aprobar, reenviar,
  // refrescar, polling) trae los mismos datos actualizados, así que vaciar las tarjetas mientras
  // llega la respuesta sólo produce un parpadeo. La lista sólo se limpia cuando cambia la búsqueda
  // (empresa / flujo / DNI), que no pasa por acá.
  const refetch = useCallback((options?: { silent?: boolean }) => {
    isSilentRefetchRef.current = options?.silent !== false;
    setRefetchKey((prev) => prev + 1);
  }, []);

  // Un guardia puede quedar mirando la lista esperando que un manager apruebe; sin polling,
  // nunca se enteraría sin cambiar de empresa o refrescar la página. Silencioso para no
  // interrumpirlo con un parpadeo de "Buscando autorizaciones…" cada 30s.
  useEffect(() => {
    const interval = setInterval(() => refetch({ silent: true }), 30000);
    return () => clearInterval(interval);
  }, [refetch]);

  const requestAuthorization = useCallback(
    async (
      payload: Omit<RequestTransportAuthorizationPayload, "token">
    ): Promise<RequestAuthorizationResult> => {
      if (!authStore.token) {
        return { success: false, error: "Sesión inválida" };
      }
      try {
        const response = await transportApi.requestTransportAuthorization({
          ...payload,
          token: authStore.token,
        });
        refetch({ silent: true });
        return { success: true, id: response.data.id };
      } catch (err) {
        console.error("Error requesting transport authorization:", err);
        return {
          success: false,
          error: typeof err === "string" ? err : "Error al solicitar la autorización",
        };
      }
    },
    [authStore.token, transportApi, refetch]
  );

  const resendAuthorization = useCallback(
    async (id: string): Promise<RequestAuthorizationResult> => {
      if (!authStore.token) {
        return { success: false, error: "Sesión inválida" };
      }
      try {
        const response = await transportApi.resendTransportAuthorizationRequest(
          authStore.token,
          id
        );
        refetch({ silent: true });
        return { success: true, id: response.data.id };
      } catch (err) {
        console.error("Error resending transport authorization request:", err);
        return {
          success: false,
          error: typeof err === "string" ? err : "Error al reenviar la notificación",
        };
      }
    },
    [authStore.token, transportApi, refetch]
  );

  // El guardia aprueba una solicitud pendiente que consiguió por fuera de la app. El refetch es
  // silencioso: la lista se actualiza sola cuando llega la respuesta, sin vaciar las tarjetas.
  const approveAuthorization = useCallback(
    async (
      payload: Omit<ApproveTransportAuthorizationPayload, "token">
    ): Promise<RequestAuthorizationResult> => {
      if (!authStore.token) {
        return { success: false, error: "Sesión inválida" };
      }
      try {
        const response = await transportApi.approveTransportAuthorization({
          ...payload,
          token: authStore.token,
        });
        refetch({ silent: true });
        return { success: true, id: response.data.id };
      } catch (err) {
        console.error("Error approving transport authorization:", err);
        return {
          success: false,
          error: typeof err === "string" ? err : "Error al registrar la autorización",
        };
      }
    },
    [authStore.token, transportApi, refetch]
  );

  return {
    authorizations,
    isLoading,
    error,
    refetch,
    requestAuthorization,
    resendAuthorization,
    approveAuthorization,
  };
};

export default useTransportAuthorizations;
